export const THEME_STORAGE_KEY = "offertli.theme.v1";
export const DARK_THEME_CLASS = "offertli-dark";
export const THEME_MEDIA_QUERY = "(prefers-color-scheme: dark)";

export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = Exclude<ThemePreference, "system">;

const THEME_PREFERENCES = new Set<ThemePreference>(["system", "light", "dark"]);

export function readThemePreference(storage: Storage): ThemePreference {
  const stored = storage.getItem(THEME_STORAGE_KEY);
  return THEME_PREFERENCES.has(stored as ThemePreference)
    ? (stored as ThemePreference)
    : "system";
}

export function writeThemePreference(
  storage: Storage,
  preference: ThemePreference,
): void {
  storage.setItem(THEME_STORAGE_KEY, preference);
}

export function resolveThemePreference(
  preference: ThemePreference,
  systemDark: boolean,
): ResolvedTheme {
  return preference === "system" ? (systemDark ? "dark" : "light") : preference;
}

export function applyThemePreference(
  root: HTMLElement,
  preference: ThemePreference,
  systemDark: boolean,
): ResolvedTheme {
  const resolved = resolveThemePreference(preference, systemDark);
  root.classList.toggle(DARK_THEME_CLASS, resolved === "dark");
  root.style.colorScheme = resolved;
  return resolved;
}

export function subscribeToSystemTheme(
  root: HTMLElement,
  preference: ThemePreference,
  media: MediaQueryList,
): () => void {
  applyThemePreference(root, preference, media.matches);
  if (preference !== "system") return () => undefined;

  const handleChange = (event: MediaQueryListEvent) => {
    applyThemePreference(root, "system", event.matches);
  };
  media.addEventListener("change", handleChange);
  return () => media.removeEventListener("change", handleChange);
}
